import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { Box, Stack } from "@mui/material";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import SortedDataGridPro6 from "../../../../../design/sorted-datagrid-pro-6";
import SkeletonLoadingOverlay from "../../../../../utils/use-skeleton-loading-overlay";
import useTableHeight from "../../../../../utils/use-table-height";
import {
  LOCATIONS, USER_ROLES, USER_STATUS,
  WORK_CENTERS
} from "../../../../filters/constants";
import useGatAdminConfigData from "../../../controllers/use-gat-admin-config-data";
import {
	closeUserDeactivateDialog,
	resetUserStatus,
} from "../../../store/gat-admin-slice";
import { HR_MANAGER } from "../constant";
import ConfirmDialog from "./company-user-dialog/confirm-dialog";
import useSupervisorsData from "./controllers/use-supervisors-data";

interface SupervisorTableProps {
	setCompanyUserData: (value: any) => void;
	setWorkCenterData: (value: any) => void;
	searchText: string;
  appliedFilters: any;
  columns: any;
}

const SupervisorTable: React.FC<SupervisorTableProps> = ({
	setCompanyUserData,
	setWorkCenterData,
	searchText,
  appliedFilters,
  columns,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const {
		companyUsers,
		getCompanyUsersStatus,
		getCompanyUsersErrorMessage,
		isUserDeactivateDialogOpen,
		userDeactivateData,
		changeUserStatusErrorMessage,
		updateUserStatus,
	} = useSupervisorsData();
  const {
    allCompanyUserRoles
  } = useGatAdminConfigData();

	const [companyUsersArray, setCompanyUsersArray] = useState([]);
	const { boxRef } = useTableHeight();

	useEffect(() => {
		try {
			// Injecting setLocationData into data
			const newCompanyUsers: any = [];
      const validCompanyUserRoles = allCompanyUserRoles?.map(
        companyUserRole => companyUserRole.id
      );
      // Only use company users where roles exists in userRoles
      const validCompanyUsers = companyUsers?.filter(
        companyUser => companyUser.roles.find(role => validCompanyUserRoles.includes(role.id))
      );

			if (validCompanyUsers.length > 0) {
        validCompanyUsers.forEach((user: any) => {
					newCompanyUsers.push({
						...user,
						changeCompanyUserData: (value: any) => setCompanyUserData(value),
						changeWorkCenterData: (value: any) => setWorkCenterData(value),
					});
				});
        const filteredUsersByLocations = appliedFilters
          .find(
            (appliedFilter: { type: string }) => appliedFilter.type === LOCATIONS
          )
          ?.data.map((location: any) => location.id);
        const filteredUsersByWorkcenters = appliedFilters
          .find(
            (appliedFilter: { type: string }) =>
              appliedFilter.type === WORK_CENTERS
          )
          ?.data.map((workCenter: any) => workCenter.id);
        const filteredUsersByRoles = appliedFilters
          .find(
            (appliedFilter: { type: string }) => appliedFilter.type === USER_ROLES
          )
          ?.data.map((role: any) => role.id);
        const filteredUsersByStatus = appliedFilters
          .find(
            (appliedFilter: { type: string }) => appliedFilter.type === USER_STATUS
          )
          ?.data.map((status: any) => status.id);
        const users = newCompanyUsers.map((user: any) => ({
          ...user,
          locations: user.userWorkCenters.map((workCenter: any) => workCenter.location),
        }));
        const finalRows = users.filter((user: any) => {
          let includeUser = true;
          if (includeUser && filteredUsersByLocations) {
            includeUser = user.locations?.find((location: any) => filteredUsersByLocations.includes(location.id));
          }
          if (includeUser && filteredUsersByWorkcenters) {
            includeUser = user.userWorkCenters?.find((workCenter: any) => filteredUsersByWorkcenters.includes(workCenter.id));
          }
          if (includeUser && filteredUsersByRoles) {
            // Remove ShiftSupervisor if HR Manager exists in roles
            const userIsHRManager = user.roles.find(role => role.name === HR_MANAGER);
            let modifiedUser = {...user};
            if (userIsHRManager) {
              modifiedUser = {
                ...modifiedUser,
                roles: modifiedUser.roles.filter(role => role.name === HR_MANAGER),
              }
            }
            includeUser = modifiedUser.roles.some((role: any) =>
              filteredUsersByRoles.includes(role.id)
            );
          }
          if (includeUser && filteredUsersByStatus) {
            includeUser = filteredUsersByStatus.includes(user.isActive ? 0 : 1);
          }
          return includeUser;
        });
				if (searchText === "") {
					setCompanyUsersArray(finalRows);
				} else {
					const filteredUsers = finalRows.filter((user: any) => {
						const fullName = `${user.firstName} ${user.lastName}`;
						const email = user.email
							.toLowerCase()
							.includes(searchText.toLowerCase());
						const workcentersAccess = user.userWorkCenters.some(
							(workcenter: any) =>
								workcenter.name.toLowerCase().includes(searchText.toLowerCase())
						);

						const wcLength = user.userWorkCenters.length
							.toString()
							.includes(searchText.toLowerCase());

						const userRoles = user.roles.some((role: any) =>
							role.name.toLowerCase().includes(searchText.toLowerCase())
						);
						const status = (user.isActive ? t("users_active") : t("users_inactive"))
							.toLowerCase()
							.includes(searchText.toLowerCase());
						const managerName = user?.managerName?.toLowerCase().includes(searchText.toLowerCase());
						return (
							fullName.toLowerCase().includes(searchText.toLowerCase()) ||
							email ||
							workcentersAccess ||
							status ||
							userRoles ||
							wcLength ||
							managerName
						);
					});
					setCompanyUsersArray(filteredUsers);
				}
			} else {
				setCompanyUsersArray([]);
			}
		} catch (e) {
			// Do nothing
		}
	}, [companyUsers, searchText, setCompanyUserData, setWorkCenterData, appliedFilters, allCompanyUserRoles, t]);

	return (
		<Box
			ref={boxRef}
			style={{
				overflow: "hidden",
				height: "calc(100vh - 252px)",
				width: "100%",
				minHeight: "200px",
			}}
		>
			<SortedDataGridPro6
				columns={columns}
				rows={companyUsersArray}
				getRowHeight={() => "auto"}
				loading={getCompanyUsersStatus === "pending"}
				pagination
				disableRowSelectionOnClick
				disableColumnSelector
				disableDensitySelector
				disableColumnReorder
				disableColumnMenu
				initialState={{
					// ...companyUsersArray.initialState,
					pagination: { paginationModel: { pageSize: 25 } },
				}}
				pageSizeOptions={[25, 50, 100]}
				pinnedColumns={{ right: ["actions"] }}
				scrollbarSize={6}
				sx={{
          overflow: "hidden",
					border: "none !important",
          "& .MuiDataGrid-columnHeader": {
            "&:first-of-type": {
              paddingLeft: "24px",
            },
            "&.hover-column-header": {
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none",
              },
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
          },
          "& .MuiDataGrid-cell": {
            "&:first-of-type": {
              paddingLeft: "24px",
            },
          },
					"& .MuiDataGrid-selectedRowCount": {
						display: "none !important",
					},
					"& .MuiDataGrid-toolbar": {
						justifyContent: "flex-end",
						"& .MuiTablePagination-root": {
							marginLeft: "auto",
						},
					},
					".MuiDataGrid-cell:focus": {
						outline: "none",
					},
					"& .MuiDataGrid-row": {
						cursor: "pointer",
						minHeight: 50,
					},
					"& .MuiTablePagination-root": {
						marginRight: "60px",
					},
					".MuiDataGrid-cell": {
						lineHeight: "50px",
						"&:focus": {
							outline: "none",
						},
						"&.hover-column-cell": {
							position: "sticky",
							right: 0,
							zIndex: 1,
						},
						"& .hover-content": {
							transition: "opacity 0.3s ease-in-out",
							opacity: 0,
						},
					},
					".MuiDataGrid-columnHeader:hover": {
						"& .MuiDataGrid-columnSeparator": {
							display: "flex",
						},
					},
					".MuiDataGrid-row:hover .hover-column-cell .hover-content": {
						opacity: 1,
						backgroundColor: "#f5f5f5",
					},
					"& .MuiDataGrid-row.Mui-selected .hover-column-cell .hover-content": {
						backgroundColor: "#e7e9f0",
					},
					"& .actions-col-header": {
						paddingLeft: "10px !important",
						paddingRight: "10px !important",
					},
					"& .actions-col-cell": {
						paddingLeft: "10px !important",
						paddingRight: "10px !important",
					},
				}}
				slots={{
          loadingOverlay: () => (
            <SkeletonLoadingOverlay
              columnData={{
                columnName: "name",
                columnStyle: { marginLeft: "12px" },
              }}
            />
          ),
					noRowsOverlay: () => (
						<Stack height='100%' alignItems='center' justifyContent='center'>
							<Box
								sx={{
									position: "relative",
									height: "32px",
									width: "32px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<PersonOffOutlinedIcon
									sx={{
										width: "24px",
										height: "24px",
										color: "rgba(0, 0, 0, 0.38)",
									}}
								/>
							</Box>
							<Typography
								sx={{
									fontFamily: "Roboto",
									fontSize: "14px",
									fontWeight: 400,
									color: "rgba(0, 0, 0, 0.38)",
									marginTop: "4px",
									textAlign: "center",
								}}
							>
								{getCompanyUsersErrorMessage !== ""
									? t("users_error-message", { message: getCompanyUsersErrorMessage })
									: t("users_no-user-added")}
							</Typography>
						</Stack>
					),
				}}
			/>

			{isUserDeactivateDialogOpen && (
				<ConfirmDialog
					open={isUserDeactivateDialogOpen}
					onClose={() => dispatch(closeUserDeactivateDialog())}
					action={() => {
						dispatch(closeUserDeactivateDialog());
					}}
					text='Are you sure that you want to update the job status?'
					item={userDeactivateData}
				/>
			)}

			<Snackbar
				open={updateUserStatus === "error"}
				autoHideDuration={3000}
				onClose={() => {
					dispatch(resetUserStatus());
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{changeUserStatusErrorMessage ?? t("common_error-try-again")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={updateUserStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => {
					dispatch(resetUserStatus());
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("users_status-updated")}
				</MuiAlert>
			</Snackbar>
		</Box>
	);
};

export default SupervisorTable;
